import React from 'react';
import { useTranslation } from 'react-i18next';
import { MigrationStatus } from '../../../../api/General/blobMigration';
import { StatusBadge } from '../../../../components/ui/StatusBadge';
import { Spinner } from '../../../../components/ui/Spinner';
import Button from '../../../../components/ui/Button';
import AppIcon from '../../../../components/AppIcon';

interface MigrationStatusPanelProps {
  status: MigrationStatus | null;
  loadingStatus: boolean;
  onRefresh: () => void;
}

export default function MigrationStatusPanel({
  status,
  loadingStatus,
  onRefresh,
}: MigrationStatusPanelProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <h3 className="text-lg font-semibold text-foreground">{t('Migration Status')}</h3>
        <Button
          variant="ghost"
          size="sm"
          iconName="RefreshCw"
          onClick={onRefresh}
          disabled={loadingStatus}
        >
          {t('Refresh')}
        </Button>
      </div>
      <div className="p-6">
        {loadingStatus ? (
          <div className="flex justify-center py-4">
            <Spinner size="sm" />
          </div>
        ) : status ? (
          <div className="flex flex-col gap-4">
            {/* Summary badges */}
            <div className="flex flex-wrap gap-2">
              <StatusBadge
                variant={status.totalPending > 0 ? 'warning' : 'neutral'}
                icon={AppIcon as any}
                withDot
              >
                {t('Pending')}: {status.totalPending}
              </StatusBadge>
              <StatusBadge variant="success" withDot>
                {t('Migrated')}: {status.totalMigrated}
              </StatusBadge>
              <StatusBadge
                variant={status.totalFailed > 0 ? 'error' : 'neutral'}
                withDot
              >
                {t('Failed')}: {status.totalFailed}
              </StatusBadge>
            </div>

            {/* Detailed breakdown */}
            <div>
              <p className="mb-2 text-sm font-medium text-foreground">
                {t('Pending by Category')}:
              </p>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-1">
                <p className="text-sm text-muted-foreground">{t('Profile Pictures')}: {status.pendingUsers}</p>
                <p className="text-sm text-muted-foreground">{t('Face Images')}: {status.pendingFaceImages}</p>
                <p className="text-sm text-muted-foreground">{t('Shift Images')}: {status.pendingShifts}</p>
                <p className="text-sm text-muted-foreground">{t('Break Images')}: {status.pendingBreaks}</p>
                <p className="text-sm text-muted-foreground">{t('Work Session Images')}: {status.pendingWorkSessions}</p>
                <p className="text-sm text-muted-foreground">{t('User Attachments')}: {status.pendingAttachments}</p>
                <p className="text-sm text-muted-foreground">{t('Leave Attachments')}: {status.pendingLeaveAttachments}</p>
                <p className="text-sm text-muted-foreground">{t('Claim Attachments')}: {status.pendingClaimAttachments}</p>
                <p className="text-sm text-muted-foreground">{t('Log Attachments')}: {status.pendingLogAttachments}</p>
                <p className="text-sm text-muted-foreground">{t('Payslips')}: {status.pendingPayslips}</p>
                <p className="text-sm text-muted-foreground">{t('Company Logos')}: {status.pendingCompanyLogos}</p>
                <p className="text-sm text-muted-foreground">{t('Payslip Branding')}: {status.pendingPayslipBranding}</p>
                <p className="text-sm text-muted-foreground">{t('Notification Images')}: {status.pendingNotificationImages}</p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('No status available')}</p>
        )}
      </div>
    </div>
  );
}
